import {
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'
import { Bar, Doughnut } from 'react-chartjs-2'
import { useNavigate, useParams } from 'react-router-dom'

import { BackLink } from '../../../components/BackLink'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { DetailField } from '../../../components/DetailField'
import { DetailHero, HeroButton, HeroLinkButton } from '../../../components/DetailHero'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TrophyIcon } from '../../../components/icons'
import { TableSkeletonRows } from '../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { RevenueSummaryCards } from '../../../components/revenueSummary/RevenueSummaryCards'
import { BulkActionBar, SelectAllCheckbox, SelectRowCheckbox, useRowSelection } from '../../../components/RowSelection'
import { SectionCard, SectionTitle, StatCard } from '../../../components/SectionCard'
import { SearchableSelect } from '../../../components/Select'
import { IStatusConfig, PAYMENT_STATUS, StatusBadge } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import {
  buildEventPreviewUrl,
  contestPublicUrl,
  formatCurrency,
  formatCurrencyValue,
  formatDate,
  formatPhone,
  FRONTEND_URL,
  useAppToast,
  useDownload,
  useHasRole,
  WEBSITE_URL
} from '../../../helpers'
import { EventService } from '../../../services/Event/EventService'
import {
  DailyStat,
  EventContest,
  EventDetail,
  EventFormat,
  EventParticipant,
  TicketWithParticipants,
  SelectEvent
} from '../../../services/Event/types'
import { magenta } from '../../../theme'
import { freeCouponCategoryColors, freeCouponCategoryShortLabels } from '../EventCoupons/couponLabels'
import EventCertificatesModal from './certificates/EventCertificatesModal'
import EventCostsSectionModal from './EventCostsSectionModal'
import ParticipantQrCodeModal from './ParticipantQrCodeModal'
import EventSurveyModal from './survey/EventSurveyModal'
import EventSurveySendModal from './survey/EventSurveySendModal'

Chart.register(...registerables)

const formatLabels: Record<EventFormat, string> = {
  IN_PERSON: 'Presencial',
  ONLINE: 'Online',
  HYBRID: 'Híbrido'
}

const formatColorScheme: Record<EventFormat, string> = {
  IN_PERSON: 'orange',
  ONLINE: 'blue',
  HYBRID: 'purple'
}

const formatIcons: Record<EventFormat, string> = {
  IN_PERSON: 'lni lni-map-marker',
  ONLINE: 'lni lni-display',
  HYBRID: 'lni lni-network'
}

const doughnutOptions = {
  cutout: '70%',
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  responsive: true,
  maintainAspectRatio: true
}

// A rodinha do card de cortesias mede 72px e o tooltip do Chart.js é desenhado dentro do
// canvas, então ele sairia cortado. Quem explica as fatias ali é um Tooltip do Chakra,
// que abre em portal e não fica preso ao card.
const miniDoughnutOptions = {
  ...doughnutOptions,
  plugins: { legend: { display: false }, tooltip: { enabled: false } }
}

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const [event, setEvent] = useState<EventDetail | null>(null)
  const [costsTotal, setCostsTotal] = useState<number>(0)
  const [eventOptions, setEventOptions] = useState<SelectEvent[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const eventService = new EventService()
  const { isDownloading, download } = useDownload()
  const hasRole = useHasRole()
  const permissions = {
    viewCoupons: hasRole('view_coupons'),
    sendCertificates: hasRole('send_certificate_event'),
    manageSurvey: hasRole('manage_event_survey'),
    viewSurvey: hasRole('view_event_survey')
  }
  const { isOpen: isCostsOpen, onOpen: onCostsOpen, onClose: onCostsClose } = useDisclosure()
  const { isOpen: isCertificatesOpen, onOpen: onCertificatesOpen, onClose: onCertificatesClose } = useDisclosure()
  const { isOpen: isSurveyOpen, onOpen: onSurveyOpen, onClose: onSurveyClose } = useDisclosure()
  const { isOpen: isSurveySendOpen, onOpen: onSurveySendOpen, onClose: onSurveySendClose } = useDisclosure()

  useEffect(() => {
    eventService
      .listForSelect()
      .then((data) => setEventOptions(data))
      .catch(() => {
        toast.error('Não foi possível carregar os outros eventos')
      })
  }, [])

  useEffect(() => {
    setLoading(true)
    setEvent(null)

    eventService
      .getBySlug(slug!)
      .then((data) => {
        setEvent(data)
        setCostsTotal(Number(data.total_costs) || 0)
      })
      .catch(() => {
        toast.error('Não foi possível carregar os detalhes do evento')
        navigate('/events')
      })
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return <EventDetailSkeleton />
  }

  if (!event) {
    return null
  }

  const tickets = event.tickets
  const totalQuantity = tickets.reduce((sum, t) => sum + t.quantity, 0)
  const totalPurchased = event.total_participants
  const percentage = totalQuantity > 0 ? Math.round((totalPurchased / totalQuantity) * 100) : 0

  const hasAddress = event.format === 'IN_PERSON' || event.format === 'HYBRID'
  const address = hasAddress
    ? [event.street, event.number].filter(Boolean).join(', ') +
      (event.neighborhood ? ` - ${event.neighborhood}` : '') +
      (event.city ? `, ${event.city}` : '') +
      (event.state ? `/${event.state}` : '')
    : null

  const dateDisplay = event.is_multiday
    ? `${formatDate(event.date_start)} - ${formatDate(event.date_end!)}`
    : formatDate(event.date_start)

  const timeDisplay = event.time_start + (event.time_end ? ` - ${event.time_end}` : '')

  return (
    <>
      {/* Top Navigation Bar */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={5}
      >
        <BackLink to="/events">Voltar para Eventos</BackLink>

        {/* Quick Event Selector */}
        <SearchableSelect
          options={eventOptions.map((opt) => ({ value: opt.slug, label: opt.name }))}
          value={event.slug}
          onChange={(val) => {
            if (val !== event.slug) navigate(`/events/${val}`)
          }}
          placeholder="Selecionar evento..."
        />
      </Flex>

      <DetailHero
        kicker="Evento"
        image={event.thumb_wide || event.thumb}
        title={event.name}
        subtitle={
          <>
            {dateDisplay} · {timeDisplay}
            {hasAddress && address ? ` · ${address}` : ''}
          </>
        }
        badges={
          <>
            <Badge
              colorScheme={formatColorScheme[event.format]}
              py={0.5}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              <i className={formatIcons[event.format]} style={{ marginRight: 4 }} />
              {formatLabels[event.format]}
            </Badge>
            <Badge
              colorScheme={event.visible ? 'green' : 'red'}
              py={0.5}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {event.visible ? 'Visível' : 'Oculto'}
            </Badge>
            {event.edition_number && (
              <Tooltip label={event.series ? `Série: ${event.series.name}` : ''} isDisabled={!event.series}>
                <Badge colorScheme="purple" py={0.5} textTransform="uppercase" letterSpacing="wider">
                  Edição {event.edition_number}
                </Badge>
              </Tooltip>
            )}
          </>
        }
        actions={
          <>
            <HeroLinkButton
              label={event.visible ? 'Página do evento' : 'Abrir preview'}
              url={event.visible ? `${WEBSITE_URL}/eventos/${event.slug}` : buildEventPreviewUrl(WEBSITE_URL, event)}
            />
            <HeroButton
              icon="lni lni-download"
              isLoading={isDownloading}
              onClick={() =>
                download(() => eventService.downloadParticipantsXlsx(event.id), 'Erro ao exportar participantes')
              }
            >
              Exportar participantes
            </HeroButton>
            {permissions.viewCoupons && (
              <HeroButton icon="lni lni-offer" href={`${FRONTEND_URL}/event-coupons?event=${event.slug}`}>
                Cupons
              </HeroButton>
            )}
            <HeroButton icon="lni lni-coin" onClick={onCostsOpen}>
              Custos
            </HeroButton>
            <HeroButton icon="lni lni-shopping-basket" to={`/events/${event.slug}/orders`}>
              Compras
            </HeroButton>
            {/* ABRA-317 — só quando o evento tem estabelecimento cadastrado. */}
            {(event.establishments_count ?? 0) > 0 && (
              <HeroButton icon="lni lni-handshake" to={`/events/${event.slug}/establishments`}>
                Vendas de parceiros
              </HeroButton>
            )}
            {permissions.sendCertificates && (
              <HeroButton icon="lni lni-certificate" onClick={onCertificatesOpen}>
                Certificados
              </HeroButton>
            )}
            {/* ABRA-370 — configurar e acompanhar são botões separados: os resultados não ficam
                escondidos atrás do editor, e só aparecem depois que a pesquisa existe. */}
            {permissions.manageSurvey && (
              <HeroButton icon="lni lni-comments" onClick={onSurveyOpen}>
                Pesquisa NPS
              </HeroButton>
            )}
            {permissions.manageSurvey && event.has_survey && (
              <HeroButton icon="lni lni-envelope" onClick={onSurveySendOpen}>
                Enviar pesquisa
              </HeroButton>
            )}
            {permissions.viewSurvey && event.has_survey && (
              <HeroButton icon="lni lni-bar-chart" to={`/events/${event.slug}/pesquisa`}>
                Resultados NPS
              </HeroButton>
            )}
          </>
        }
      />

      {/* Stat cards row */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: event.contests.length > 0 ? 4 : 2 }} spacing={4} mb={6}>
        <StatCard
          label="Ingressos"
          value={`${totalPurchased}/${totalQuantity}`}
          helpText={totalQuantity > 0 ? `${percentage}% ocupado` : 'Nenhum cadastrado'}
          color="abraPurple.500"
          icon="lni lni-ticket"
        />
        {event.contests.length > 0 && (
          <StatCard
            label="Ingressos Concursos"
            value={event.contest_participants_count.toString()}
            helpText="Participantes via concurso"
            color="orange.500"
            icon={<TrophyIcon />}
          />
        )}
        {event.contests.length > 0 && (
          <StatCard
            label="Ingressos Evento"
            value={event.direct_participants_count.toString()}
            helpText="Compra direta"
            color="blue.500"
            icon="lni lni-users"
          />
        )}
        <FreeTicketsStatCard
          total={event.free_participants_count}
          breakdown={event.free_participants_by_category ?? []}
        />
      </SimpleGrid>

      {/* Revenue cards */}
      <RevenueSummaryCards
        eventSlug={event.slug}
        eventId={event.id}
        hasContests={event.contests.length > 0}
        costsTotal={costsTotal}
      />

      {/* Main content: two columns */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} mb={6}>
        {/* Left: overall ticket chart */}
        <GridItem>
          <SectionCard title="Visão Geral de Ingressos" h="100%">
            {tickets.length === 0 ? (
              <Flex align="center" justify="center" h="200px">
                <Text variant="caption">Nenhum ingresso cadastrado</Text>
              </Flex>
            ) : (
              <Flex align="center" justify="center" gap={8} direction={{ base: 'column', md: 'row' }}>
                <Box w="180px" h="180px" position="relative">
                  <Doughnut
                    data={{
                      labels: ['Inscritos', 'Disponíveis'],
                      datasets: [
                        {
                          data: [totalPurchased, Math.max(0, totalQuantity - totalPurchased)],
                          backgroundColor: [magenta[500], '#EDF2F7'],
                          borderWidth: 0,
                          borderRadius: 4
                        }
                      ]
                    }}
                    options={doughnutOptions}
                  />
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    direction="column"
                    align="center"
                  >
                    <Text fontSize="2xl" fontWeight="800" lineHeight="1" color="gray.800">
                      {percentage}%
                    </Text>
                    <Text variant="caption">ocupado</Text>
                  </Flex>
                </Box>
                <VStack align="start" spacing={3}>
                  <LegendItem color="magenta.500" label="Inscritos" value={totalPurchased} />
                  <LegendItem
                    color="gray.100"
                    label="Disponíveis"
                    value={Math.max(0, totalQuantity - totalPurchased)}
                  />
                  <Divider />
                  <Text variant="caption">
                    Total: <strong>{totalQuantity}</strong> ingressos
                  </Text>
                </VStack>
              </Flex>
            )}
          </SectionCard>
        </GridItem>

        {/* Right: info card */}
        <GridItem>
          <SectionCard title="Informações do Evento" h="100%">
            <VStack align="stretch" spacing={4}>
              <DetailField icon="lni lni-calendar" label="Data" value={dateDisplay} />
              <DetailField icon="lni lni-alarm-clock" label="Horário" value={timeDisplay} />
              <DetailField icon={formatIcons[event.format]} label="Formato" value={formatLabels[event.format]} />
              {hasAddress && address && <DetailField icon="lni lni-map-marker" label="Local" value={address} />}
              <DetailField
                icon="lni lni-tag"
                label="Categorias"
                value={event.types.map((t) => t.name).join(', ') || '-'}
              />
            </VStack>
          </SectionCard>
        </GridItem>
      </Grid>

      {/* Per-ticket breakdown */}
      {tickets.length > 0 && (
        <Box mb={6}>
          <SectionTitle>Ingressos por Tipo</SectionTitle>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {tickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Participants */}
      <ParticipantsSection eventId={event.id} tickets={event.tickets} />

      {/* Contests */}
      {event.contests.length > 0 && (
        <Box mb={6}>
          <SectionTitle>Concursos</SectionTitle>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {event.contests.map((contest) => (
              <ContestCard key={contest.id} contest={contest} />
            ))}
          </SimpleGrid>
        </Box>
      )}
      <EventCostsSectionModal
        isOpen={isCostsOpen}
        onClose={onCostsClose}
        eventId={event.id}
        onTotalChange={setCostsTotal}
      />
      {permissions.sendCertificates && (
        <EventCertificatesModal
          isOpen={isCertificatesOpen}
          onClose={onCertificatesClose}
          eventId={event.id}
          eventName={event.name}
          onConfigureSurvey={permissions.manageSurvey ? onSurveyOpen : undefined}
        />
      )}
      {permissions.manageSurvey && (
        <EventSurveyModal
          isOpen={isSurveyOpen}
          onClose={onSurveyClose}
          eventId={event.id}
          eventName={event.name}
          eventSlug={event.slug}
          eventImage={event.thumb_wide || event.thumb}
          canViewResults={permissions.viewSurvey}
          onSaved={() => setEvent((current) => (current ? { ...current, has_survey: true } : current))}
        />
      )}
      {permissions.manageSurvey && event.has_survey && (
        <EventSurveySendModal
          isOpen={isSurveySendOpen}
          onClose={onSurveySendClose}
          eventId={event.id}
          eventName={event.name}
        />
      )}
    </>
  )
}

/* ─── Participants Section ──────────────────────────────── */

const barOptions = {
  responsive: true,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true }
  },
  scales: {
    y: { beginAtZero: true, ticks: { stepSize: 1 } },
    x: { grid: { display: false } }
  }
}

function ParticipantsSection({ eventId, tickets }: { eventId: number; tickets: TicketWithParticipants[] }) {
  const service = new EventService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const hasSendEmailRole = hasRole('send_ticket_email_event')
  const { isOpen: isEmailAlertOpen, onOpen: onEmailAlertOpen, onClose: onEmailAlertClose } = useDisclosure()

  const [participants, setParticipants] = useState<EventParticipant[]>([])
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([])
  const [search, setSearch] = useState('')
  const [ticketFilter, setTicketFilter] = useState('')
  const [originFilter, setOriginFilter] = useState('')
  const [paidValueFilter, setPaidValueFilter] = useState('')
  const [paidValues, setPaidValues] = useState<number[]>([])
  const [paidValuesLoading, setPaidValuesLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalFiltered, setTotalFiltered] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [qrData, setQrData] = useState<{
    token: string
    participant_name: string
    event_name: string
    ticket_name: string
  } | null>(null)
  const [qrLoading, setQrLoading] = useState<number | null>(null)

  // Email sending state
  const [sendAll, setSendAll] = useState(false)
  const [emailSending, setEmailSending] = useState(false)

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Só quem pagou (ou teve a caução devolvida) recebe o QR Code, então a linha de quem não
  // pagou não é selecionável — e também não conta para o "marcar todos".
  const selection = useRowSelection(participants, {
    isSelectable: (p) => p.payment_status === 'paid' || ['refund_pending', 'refunded'].includes(p.payment_status)
  })

  // Load paid value options on mount
  useEffect(() => {
    service
      .getParticipantPaidValues(eventId)
      .then((data) => {
        setPaidValues(data.paid_values)
      })
      .catch(() => {
        // silently fail
      })
      .finally(() => {
        setPaidValuesLoading(false)
      })
  }, [eventId])

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchParticipants()
    }, 500)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, ticketFilter, originFilter, paidValueFilter, page, pageSize])

  // A seleção em si é podada pelo `useRowSelection` quando as linhas mudam; o que sobra aqui é
  // o "enviar para todos", que é decisão desta tela.
  useEffect(() => {
    setSendAll(false)
  }, [page, search, ticketFilter, originFilter, paidValueFilter])

  function fetchParticipants() {
    setLoading(true)
    setLoadError(false)
    service
      .getParticipants(eventId, {
        search: search || undefined,
        ticket_id: ticketFilter ? Number(ticketFilter) : undefined,
        origin: originFilter || undefined,
        paid_value: paidValueFilter ? Number(paidValueFilter) : undefined,
        page,
        page_size: pageSize
      })
      .then((res) => {
        setParticipants(res.data)
        setTotal(res.total)
        setTotalFiltered(res.total_filtered)
        setDailyStats(res.daily_stats)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os inscritos', error)
      })
      .finally(() => setLoading(false))
  }

  const totalPages = Math.ceil(totalFiltered / pageSize)

  const handleShowQrCode = async (participant: EventParticipant) => {
    setQrLoading(participant.id)
    try {
      const data = await service.getParticipantTicketQrCode(eventId, participant.id)
      setQrData(data)
      setQrModalOpen(true)
    } catch (e) {
      // silently fail
    } finally {
      setQrLoading(null)
    }
  }

  async function handleSendToOne(participantId: number) {
    try {
      const res = await service.sendTicketEmails(eventId, { participantIds: [participantId] })
      toast.success('E-mail agendado', `${res.dispatched} e-mail agendado para envio.`)
    } catch {
      toast.error('Erro ao agendar e-mail')
    }
  }

  async function handleSendEmailConfirm() {
    setEmailSending(true)
    try {
      const options = sendAll ? { sendAll: true } : { participantIds: selection.selectedIds }
      const res = await service.sendTicketEmails(eventId, options)
      toast.success('E-mails agendados', `${res.dispatched} e-mail(s) agendado(s) para envio.`)
      handleEmailAlertClose()
    } catch {
      toast.error('Erro ao agendar e-mails')
    } finally {
      setEmailSending(false)
    }
  }

  /**
   * Unified handler for closing the email alert dialog.
   * Ensures that all dialog-related state is reset regardless of how the dialog is closed
   * (explicit "Cancelar" button, ESC key, or overlay click).
   */
  function handleEmailAlertClose() {
    onEmailAlertClose()
    selection.clear()
    setSendAll(false)
  }

  const colSpan = hasSendEmailRole ? 10 : 9

  const chartData = {
    labels: dailyStats.map((s) =>
      new Date(s.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    ),
    datasets: [
      {
        label: 'Inscrições',
        data: dailyStats.map((s) => s.count),
        backgroundColor: magenta[500],
        borderRadius: 4,
        maxBarThickness: 40
      }
    ]
  }

  return (
    <Box mb={6}>
      <SectionTitle>Inscritos ({total})</SectionTitle>

      {dailyStats.length > 1 && (
        <SectionCard title="Inscrições por dia" titleAs="h3" mb={4}>
          <Box maxH="220px" w="100%">
            <Bar data={chartData} options={{ ...barOptions, maintainAspectRatio: false }} height={220} />
          </Box>
        </SectionCard>
      )}

      <SectionCard>
        <FilterBar
          onClear={() => {
            setSearch('')
            setTicketFilter('')
            setOriginFilter('')
            setPaidValueFilter('')
            setPage(1)
          }}
          isDirty={search !== '' || ticketFilter !== '' || originFilter !== '' || paidValueFilter !== ''}
        >
          <FilterSearch
            value={search}
            onChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
            placeholder="Buscar por nome ou e-mail..."
          />
          <FilterSelect
            label="Ingresso"
            maxW={{ md: '240px' }}
            value={ticketFilter}
            onChange={(value) => {
              setTicketFilter(value)
              setPage(1)
            }}
            options={tickets.map((t) => ({ value: t.id, label: t.title }))}
            emptyLabel="Todos os ingressos"
          />
          <FilterSelect
            label="Origem"
            maxW={{ md: '200px' }}
            value={originFilter}
            onChange={(value) => {
              setOriginFilter(value)
              setPage(1)
            }}
            options={[
              { value: 'direct', label: 'Compra direta' },
              { value: 'contest', label: 'Concurso' }
            ]}
            emptyLabel="Todas as origens"
          />
          <FilterSelect
            label="Valor pago"
            maxW={{ md: '200px' }}
            value={paidValueFilter}
            onChange={(value) => {
              setPaidValueFilter(value)
              setPage(1)
            }}
            isDisabled={paidValuesLoading || paidValues.length === 0}
            options={paidValues.map((value) => ({
              value,
              label: value === 0 ? 'Gratuitos' : formatCurrency(value)
            }))}
            emptyLabel="Todos os valores"
          />
        </FilterBar>

        {/* Email send action bar */}
        {hasSendEmailRole && (
          <>
            {/* Aqui o espaço da barra já é ocupado pelo "Enviar para todos" abaixo, então ela sai
                do fluxo em vez de reservá-lo. */}
            <BulkActionBar selection={selection} entity="participante" reserveSpace={false}>
              <Button
                size="sm"
                colorScheme="purple"
                leftIcon={<Icon name="lni lni-envelope" size="md" />}
                onClick={onEmailAlertOpen}
              >
                Enviar QR Code
              </Button>
            </BulkActionBar>

            {/* Sem nada marcado a saída é o envio para a lista inteira, que não é uma ação em
                massa sobre a seleção e por isso fica fora da barra. */}
            {selection.count === 0 && (
              <Flex mb={4}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="purple"
                  leftIcon={<Icon name="lni lni-envelope" size="md" />}
                  onClick={() => {
                    setSendAll(true)
                    selection.clear()
                    onEmailAlertOpen()
                  }}
                >
                  Enviar para todos os inscritos ({total})
                </Button>
              </Flex>
            )}
          </>
        )}

        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                {hasSendEmailRole && <SelectAllCheckbox selection={selection} />}
                <Th>Nome</Th>
                <Th>E-mail</Th>
                <Th>Telefone</Th>
                <Th>Ingresso</Th>
                <Th>Valor Pago</Th>
                <Th>Cupom</Th>
                <Th>Status</Th>
                <Th>Data</Th>
                <Th w="80px">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <TableSkeletonRows cols={colSpan} />
              ) : loadError ? (
                <TableErrorRow colSpan={colSpan} entity="inscritos" onRetry={fetchParticipants} />
              ) : participants.length === 0 ? (
                <TableEmptyRow colSpan={colSpan} entity="inscrito" />
              ) : (
                participants.map((p) => {
                  const isPaid =
                    p.payment_status === 'paid' || ['refund_pending', 'refunded'].includes(p.payment_status)
                  return (
                    <Tr key={p.id}>
                      {hasSendEmailRole && <SelectRowCheckbox selection={selection} id={p.id} isDisabled={!isPaid} />}
                      <Td>
                        <Flex align="center" gap={2}>
                          <Text fontSize="sm" fontWeight="500" color="gray.800">
                            {p.name}
                          </Text>
                          {p.from_contest && <Badge colorScheme="orange">Concurso</Badge>}
                          {p.is_door_sale && <Badge colorScheme="cyan">Porta</Badge>}
                        </Flex>
                      </Td>
                      <Td>
                        <Text variant="caption">{p.email}</Text>
                      </Td>
                      <Td>
                        <Text variant="caption">{formatPhone(p.phone) || '-'}</Text>
                      </Td>

                      <Td>
                        <Badge colorScheme={p.ticket?.type === 'FREE' ? 'green' : 'purple'}>
                          {p.ticket?.title ?? '-'}
                        </Badge>
                      </Td>
                      <Td>
                        <Text variant="caption">{p.paid_value === 0 ? 'Gratuito' : formatCurrency(p.paid_value)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" fontWeight="500" color={p.coupon_code ? 'green.500' : 'red.400'}>
                          {p.coupon_code ?? 'Sem cupom'}
                        </Text>
                      </Td>
                      <Td>
                        <StatusBadge status={p.payment_status} statuses={PAYMENT_STATUS} />
                      </Td>
                      <Td>
                        <Text variant="caption">{formatDate(p.created_at)}</Text>
                      </Td>
                      <Td>
                        <Flex gap={1}>
                          <IconButton
                            aria-label="QR Code"
                            icon={<Box as="img" src="/img/icons/qr-code.png" alt="" w="18px" h="18px" />}
                            size="sm"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: 'purple.500' }}
                            isLoading={qrLoading === p.id}
                            onClick={() => handleShowQrCode(p)}
                          />
                          {hasSendEmailRole && (
                            <Tooltip label="Reenviar e-mail com QR Code">
                              <IconButton
                                aria-label="Reenviar ingresso por e-mail"
                                icon={<Icon name="lni lni-envelope" size="md" />}
                                size="sm"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'purple.500' }}
                                isDisabled={!isPaid}
                                onClick={() => handleSendToOne(p.id)}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  )
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Paginator
            page={page}
            totalPages={totalPages}
            changePage={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}

        {/* A linha aparece sempre que há inscritos: escondê-la quando o filtro casa com todos
            deixava o leitor sem saber se o filtro pegou tudo ou se foi ignorado. */}
        {!loading && total > 0 && (
          <Text variant="caption" mt={2} textAlign="center">
            Exibindo {totalFiltered} de {total} inscritos
          </Text>
        )}
      </SectionCard>

      {qrData && (
        <ParticipantQrCodeModal
          isOpen={qrModalOpen}
          onClose={() => {
            setQrModalOpen(false)
            setQrData(null)
          }}
          token={qrData.token}
          participantName={qrData.participant_name}
          eventName={qrData.event_name}
          ticketName={qrData.ticket_name}
        />
      )}

      {/* Send email confirmation dialog */}
      <ConfirmDialog
        isOpen={isEmailAlertOpen}
        onClose={handleEmailAlertClose}
        onConfirm={handleSendEmailConfirm}
        action="send"
        title="Enviar QR Code por e-mail"
        description={
          sendAll ? (
            <>
              Enviar o e-mail de ingresso (com QR Code) para <strong>todos os {total} participantes</strong>?
            </>
          ) : (
            <>
              Enviar o e-mail de ingresso (com QR Code) para os{' '}
              <strong>
                {selection.count} participante{selection.count !== 1 ? 's' : ''} selecionado
                {selection.count !== 1 ? 's' : ''}
              </strong>
              ?
            </>
          )
        }
        confirmLabel="Confirmar envio"
        isLoading={emailSending}
      />
    </Box>
  )
}

/* ─── Sub-components ─────────────────────────────────────── */

/**
 * Igual aos outros stat cards, com a rodinha no lugar do ícone e a origem das cortesias
 * na linha de apoio — o card fica na mesma grade e com a mesma altura que os vizinhos.
 *
 * Fatias zeradas saem da linha: quase todo evento usa um tipo só de cortesia, e três
 * rótulos zerados dizem menos do que o único que importa.
 */
function FreeTicketsStatCard({
  total,
  breakdown
}: {
  total: number
  breakdown: EventDetail['free_participants_by_category']
}) {
  const slices = breakdown.filter((slice) => slice.count > 0)

  return (
    <StatCard
      label="Ingressos Gratuitos"
      value={total}
      color="green.500"
      icon="lni lni-gift"
      helpText={
        slices.length === 0 ? (
          'Compras diretas gratuitas'
        ) : (
          <Flex gap={2} flexWrap="wrap" align="center">
            {slices.map((slice) => (
              <Flex key={slice.category} align="center" gap={1}>
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={freeCouponCategoryColors[slice.category] ?? '#CBD5E0'}
                  flexShrink={0}
                />
                <Text as="span" color="gray.500">
                  {freeCouponCategoryShortLabels[slice.category] ?? slice.category} {slice.count}
                </Text>
              </Flex>
            ))}
          </Flex>
        )
      }
      // Com as fatias, a rodinha substitui o ladrilho do ícone inteiro — `alignSelf` a centra na
      // altura do bloco de texto em vez de prendê-la no topo, e `mr` tira ela da borda.
      aside={
        slices.length > 0 ? (
          <Tooltip
            label={slices
              .map((slice) => `${freeCouponCategoryShortLabels[slice.category] ?? slice.category}: ${slice.count}`)
              .join(' · ')}
          >
            <Box w="72px" h="72px" flexShrink={0} alignSelf="center" mr={1}>
              <Doughnut
                data={{
                  labels: slices.map((slice) => freeCouponCategoryShortLabels[slice.category] ?? slice.category),
                  datasets: [
                    {
                      data: slices.map((slice) => slice.count),
                      backgroundColor: slices.map((slice) => freeCouponCategoryColors[slice.category] ?? '#CBD5E0'),
                      borderWidth: 0
                    }
                  ]
                }}
                options={miniDoughnutOptions}
              />
            </Box>
          </Tooltip>
        ) : undefined
      }
    />
  )
}

function LegendItem({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <Flex align="center" gap={2}>
      <Box w="10px" h="10px" borderRadius="sm" bg={color} flexShrink={0} />
      <Text variant="caption">
        {label}: <strong>{value}</strong>
      </Text>
    </Flex>
  )
}

function TicketCard({ ticket }: { ticket: TicketWithParticipants }) {
  const used = ticket.participants_count
  const total = ticket.quantity
  const available = Math.max(0, total - used)
  const pct = total > 0 ? Math.round((used / total) * 100) : 0
  const isFree = ticket.type === 'FREE'
  const isFreeCaucao = isFree && ticket.price > 0
  const cautionaryRevenue = ticket.cautionary_revenue ?? 0

  // Rótulo e cor saem juntos: separados em duas expressões com as mesmas três condições, bastava
  // alguém mexer numa para os dois passarem a dizer coisas diferentes.
  const kind: IStatusConfig = isFreeCaucao
    ? { label: 'Gratuito (Caução)', colorScheme: 'orange' }
    : isFree
      ? { label: 'Gratuito', colorScheme: 'green' }
      : { label: 'Pago', colorScheme: 'purple' }

  return (
    <SectionCard variant="tile">
      <Flex justify="space-between" align="start" mb={4}>
        <Box flex={1}>
          <Flex align="center" gap={2} mb={1}>
            <Text fontSize="sm" fontWeight="600" color="gray.800">
              {ticket.title}
            </Text>
            <StatusBadge label={kind.label} colorScheme={kind.colorScheme} />
            {ticket.is_child_ticket && <Badge colorScheme="purple">Infantil</Badge>}
          </Flex>
          {(!isFree || isFreeCaucao) && <Text variant="caption">R$ {formatCurrencyValue(ticket.price)}</Text>}
          {(!isFree || isFreeCaucao) && ticket.total_revenue > 0 && (
            <Flex align="center" gap={1}>
              <Text fontSize="sm" fontWeight="bold" color="green.500">
                {formatCurrency(ticket.total_revenue)} Arrecadados
              </Text>
              {isFreeCaucao && (
                <Tooltip label="Receita de participantes que não compareceram ao evento. Participantes que fizeram check-in terão o valor reembolsado.">
                  <Icon name="lni lni-information" cursor="help" color="blue.300" size="sm" />
                </Tooltip>
              )}
            </Flex>
          )}
          {isFreeCaucao && cautionaryRevenue > 0 && ticket.total_revenue === 0 && (
            <Flex align="center" gap={1}>
              <Text fontSize="sm" fontWeight="500" color="orange.500">
                {formatCurrency(cautionaryRevenue)} em caução
              </Text>
              <Tooltip label="Este valor será reembolsado aos participantes que realizarem check-in no evento.">
                <Icon name="lni lni-information" cursor="help" color="orange.400" size="sm" />
              </Tooltip>
            </Flex>
          )}
        </Box>
        <CircularProgress
          value={pct}
          size="56px"
          thickness="8px"
          color={isFree ? 'abraGreen.500' : 'abraPurple.500'}
          trackColor="gray.100"
        >
          <CircularProgressLabel fontSize="xs" fontWeight="bold" color="gray.700">
            {pct}%
          </CircularProgressLabel>
        </CircularProgress>
      </Flex>
      <Flex justify="space-between" fontSize="xs" color="gray.500">
        <Text>
          <Text as="span" fontWeight="600" color="gray.700">
            {used}
          </Text>{' '}
          inscritos
        </Text>
        <Text>
          <Text as="span" fontWeight="600" color="gray.700">
            {available}
          </Text>{' '}
          disponíveis
        </Text>
        <Text>
          <Text as="span" fontWeight="600" color="gray.700">
            {total}
          </Text>{' '}
          total
        </Text>
      </Flex>
    </SectionCard>
  )
}

function ContestCard({ contest }: { contest: EventContest }) {
  const navigate = useNavigate()

  const now = new Date()
  const registrationDeadline = new Date(contest.registration_deadline)
  const submissionDeadline = new Date(contest.submission_deadline)

  // Status calculado, não consultado — a mesma forma do `getArtworkSaleStatus`: rótulo e cor
  // saem no mesmo objeto, então não há como um dos dois ficar para trás.
  const status: IStatusConfig =
    now < registrationDeadline
      ? { label: 'Inscrições abertas', colorScheme: 'green' }
      : now < submissionDeadline
        ? { label: 'Envio de obras', colorScheme: 'blue' }
        : { label: 'Encerrado', colorScheme: 'gray' }

  const hasLimit = contest.max_registrations > 0
  const fillPct = hasLimit ? Math.round((contest.paid_applications_count / contest.max_registrations) * 100) : 0
  const available = hasLimit ? Math.max(0, contest.max_registrations - contest.paid_applications_count) : null

  return (
    <SectionCard variant="tile">
      <Flex justify="space-between" align="start" mb={4}>
        <Box flex={1} pr={3}>
          <Heading as="h3" color="gray.800" fontWeight="600" mb={1}>
            {contest.name}
          </Heading>
          <StatusBadge label={status.label} colorScheme={status.colorScheme} py={0.5} />
        </Box>
        {hasLimit && (
          <CircularProgress value={fillPct} size="56px" thickness="8px" color="abraGreen.500" trackColor="gray.100">
            <CircularProgressLabel fontSize="xs" fontWeight="bold" color="gray.700">
              {fillPct}%
            </CircularProgressLabel>
          </CircularProgress>
        )}
      </Flex>

      <SimpleGrid columns={3} spacing={2} mb={4}>
        <Box bg="gray.50" borderRadius="lg" p={3} textAlign="center">
          <Text fontSize="xl" fontWeight="800" color="blue.400">
            {contest.paid_applications_count}
          </Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            Inscrições
          </Text>
        </Box>
        <Box bg="gray.50" borderRadius="lg" p={3} textAlign="center">
          <Text fontSize="xl" fontWeight="800" color="abraPurple.500">
            {contest.approved_arts_count}
          </Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            Obras
          </Text>
        </Box>
        <Box bg="gray.50" borderRadius="lg" p={3} textAlign="center">
          <Text fontSize="xl" fontWeight="800" color="orange.400">
            {contest.arts_for_auction_count}
          </Text>
          <Text fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            Leilão
          </Text>
        </Box>
      </SimpleGrid>

      {hasLimit && (
        <Flex justify="space-between" fontSize="xs" color="gray.500" mb={4}>
          <Text>
            Vagas preenchidas:{' '}
            <Text as="span" fontWeight="600" color="gray.700">
              {contest.paid_applications_count}/{contest.max_registrations}
            </Text>
          </Text>
          <Text>
            Disponíveis:{' '}
            <Text as="span" fontWeight="600" color="gray.700">
              {available}
            </Text>
          </Text>
        </Flex>
      )}

      <Flex gap={2}>
        <Button size="xs" colorScheme="magenta" variant="outline" onClick={() => navigate(`/contests/${contest.slug}`)}>
          Ver detalhes →
        </Button>
        <Tooltip label="Abrir no site">
          <IconButton
            aria-label="Abrir concurso no site"
            as="a"
            href={contestPublicUrl(contest)}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="outline"
            colorScheme="gray"
            icon={<Icon name="lni lni-exit-up" size="sm" />}
          />
        </Tooltip>
      </Flex>
    </SectionCard>
  )
}

/* ─── Skeleton ─────────────────────────────────────────── */

function EventDetailSkeleton() {
  return (
    <>
      <Skeleton height="16px" width="150px" mb={5} borderRadius="md" />
      <Skeleton height="260px" borderRadius="xl" mb={6} />
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        {[1, 2, 3, 4].map((i) => (
          <SectionCard key={i} variant="tile">
            <Skeleton height="12px" width="80px" mb={2} />
            <Skeleton height="28px" width="60px" mb={1} />
            <Skeleton height="10px" width="100px" />
          </SectionCard>
        ))}
      </SimpleGrid>
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} mb={6}>
        <SectionCard>
          <Skeleton height="16px" width="200px" mb={5} />
          <Flex align="center" gap={8}>
            <SkeletonCircle size="180px" />
            <VStack align="start" spacing={3} flex={1}>
              <Skeleton height="14px" width="140px" />
              <Skeleton height="14px" width="160px" />
              <Skeleton height="14px" width="100px" />
            </VStack>
          </Flex>
        </SectionCard>
        <SectionCard>
          <Skeleton height="16px" width="180px" mb={5} />
          <VStack align="stretch" spacing={4}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Flex key={i} align="center" gap={3}>
                <Skeleton height="32px" width="32px" borderRadius="lg" />
                <Box flex={1}>
                  <Skeleton height="10px" width="50px" mb={1} />
                  <Skeleton height="14px" width="120px" />
                </Box>
              </Flex>
            ))}
          </VStack>
        </SectionCard>
      </Grid>
      <Skeleton height="16px" width="160px" mb={4} />
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {[1, 2, 3].map((i) => (
          <SectionCard key={i} variant="tile">
            <Flex justify="space-between" mb={4}>
              <Box flex={1}>
                <Skeleton height="14px" width="100px" mb={2} />
                <Skeleton height="10px" width="60px" />
              </Box>
              <SkeletonCircle size="56px" />
            </Flex>
            <SkeletonText noOfLines={1} skeletonHeight="3" />
          </SectionCard>
        ))}
      </SimpleGrid>
    </>
  )
}
